import { Component, Injector, ViewChild, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { ButtonComponent, FormsUtil, InputComponent, ModalComponent, ModalFooterDirective } from 'mbs-ui-kit';
import { InfoCardComponent } from 'mbs-ui-shared@components/info-card/info-card.component';
import { CompaniesFacade } from 'mbs-ui-shared@facades/companies.facade';
import { Company } from 'mbs-ui-shared@models';
import { filter, map, switchMap, take } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'mbs-add-company-modal',
  templateUrl: './add-company-modal.component.html',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    ButtonComponent,
    InfoCardComponent,
    I18NextPipe,
    ModalComponent,
    InputComponent,
    ModalFooterDirective
  ]
})
export class AddCompanyModalComponent {
  private fb = inject(FormBuilder);
  private companiesFacade = inject(CompaniesFacade);
  private injector = inject(Injector);
  private router = inject(Router);

  public readonly e2eLocators = {
    main: 'add-company-modal',
    companyNameControl: 'company-name-control',
    createBtn: 'create-btn'
  };

  public requestInProgress = false;
  public modalForm = this.fb.group({
    companyName: [null, [Validators.required]]
  });

  @ViewChild(ModalComponent, { static: true }) baseModal: ModalComponent;

  constructor() {
    this.companiesFacade.loadData();
  }

  goToTemplateClickHandler(): void {
    const url = this.router.serializeUrl(this.router.createUrlTree(['/AP/BackupTemplates']));

    return void window.open(url, '_blank');
  }

  public onCreateCompanyButtonClick() {
    if (!this.modalForm.valid) {
      FormsUtil.triggerValidation(this.modalForm);
      return;
    }

    this.requestInProgress = true;

    this.companiesFacade
      .create({ name: this.modalForm.value.companyName } as Company)
      .pipe(
        switchMap((company) =>
          toObservable(this.companiesFacade.data, { injector: this.injector }).pipe(
            filter((companies) => companies.some((comp) => comp.id === company.id)),
            take(1),
            map(() => company)
          )
        ),
        untilDestroyed(this)
      )
      .subscribe({
        next: (company) => this.baseModal.save(company),
        error: () => (this.requestInProgress = false)
      });
  }
}
